import { create } from 'zustand';
import * as Sentry from '@sentry/react';
import type { User } from '../types';
import { setAccessToken } from '../api/client';
import { clearOfflineCache, setCacheOwner } from '../api/offlineCache';

const SNAPSHOT_KEY = 'gromly.lastUser';

/**
 * The last signed-in user, kept so the app can open on cached trip data when
 * `/auth/refresh` can't be reached. It is not a credential: the refresh cookie
 * is still the only thing that grants access to the API, and the cache it
 * unlocks holds nothing this user hadn't already loaded.
 */
export function readUserSnapshot(): User | null {
  try {
    const raw = localStorage.getItem(SNAPSHOT_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

function writeUserSnapshot(user: User | null): void {
  try {
    if (user) localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(user));
    else localStorage.removeItem(SNAPSHOT_KEY);
  } catch {
    /* storage disabled: offline boot just won't be available */
  }
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Signed in from a snapshot, with no access token: reads come from cache. */
  isOfflineSession: boolean;
  setAuth: (user: User, token: string) => void;
  setOfflineAuth: (user: User) => void;
  clearAuth: () => void;
  setLoading: (v: boolean) => void;
  updateTripCredits: (credits: number) => void;
  updateAiCredits: (credits: number) => void;
  updatePremiumAiCredits: (credits: number) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  isOfflineSession: false,
  setAuth: (user, token) => {
    setAccessToken(token);
    setCacheOwner(user.id);
    writeUserSnapshot(user);
    Sentry.setUser({ id: user.id, username: user.username, email: user.email });
    set({ user, isAuthenticated: true, isLoading: false, isOfflineSession: false });
  },
  setOfflineAuth: (user) => {
    setAccessToken(null);
    setCacheOwner(user.id);
    Sentry.setUser({ id: user.id, username: user.username, email: user.email });
    set({ user, isAuthenticated: true, isLoading: false, isOfflineSession: true });
  },
  clearAuth: () => {
    setAccessToken(null);
    writeUserSnapshot(null);
    void clearOfflineCache();
    Sentry.setUser(null);
    set({ user: null, isAuthenticated: false, isLoading: false, isOfflineSession: false });
  },
  setLoading: (v) => set({ isLoading: v }),
  updateTripCredits: (credits) => set((state) => ({
    user: state.user ? { ...state.user, tripCredits: credits } : null,
  })),
  updateAiCredits: (credits) => set((state) => ({
    user: state.user ? { ...state.user, aiCredits: credits } : null,
  })),
  updatePremiumAiCredits: (credits) => set((state) => ({
    user: state.user ? { ...state.user, premiumAiCredits: credits } : null,
  })),
}));
